import type { FC } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type {
  CreativeDnDMovePayload,
  HypothesisGroup,
  HypothesisSection,
} from '../../types/hypothesis';
import useGroupSelectionSlices from '../../hooks/useGroupSelectionSlices';
import type { HypothesisPack } from '../../hooks';
import HypothesisGroupCard from './HypothesisGroupCard';

const EMPTY_PACKS: HypothesisPack[] = [];

interface HypothesisSectionGroupProps {
  title: string;
  groups: HypothesisGroup[];
  packsByHypothesis: Map<string, HypothesisPack[]>;
  selectedCreatives: string[];
  columns: GridColDef[];
  dragEnabled?: boolean;
  canEditPackTag?: boolean;
  packTagsByIndex?: string[];
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange: (packIndex: number, packCreativeIds: string[]) => void;
  onSectionChange: (hypothesis: string, section: HypothesisSection) => void;
  onCreativeMove?: (hypothesis: string, move: CreativeDnDMovePayload) => void;
}

const HypothesisSectionGroup: FC<HypothesisSectionGroupProps> = ({
  title,
  groups,
  packsByHypothesis,
  selectedCreatives,
  columns,
  dragEnabled = false,
  canEditPackTag = false,
  packTagsByIndex,
  onSelectionChange,
  onPackSelectionChange,
  onSectionChange,
  onCreativeMove,
}) => {
  const slices = useGroupSelectionSlices(groups, selectedCreatives);

  if (groups.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
      {groups.map((group) => {
        const packs = packsByHypothesis.get(group.hypothesis) ?? EMPTY_PACKS;
        return (
          <HypothesisGroupCard
            key={group.hypothesis}
            hypothesis={group.hypothesis}
            creatives={group.creatives}
            packs={packs}
            section={group.section}
            showSectionSelector
            selectedIds={slices.get(group.hypothesis) ?? []}
            columns={columns}
            dragEnabled={dragEnabled}
            canEditPackTag={canEditPackTag}
            packTagsByIndex={packTagsByIndex}
            onSelectionChange={onSelectionChange}
            onPackSelectionChange={onPackSelectionChange}
            onSectionChange={onSectionChange}
            onCreativeMove={onCreativeMove}
          />
        );
      })}
    </Box>
  );
};

export default HypothesisSectionGroup;
